<template>
  <HrPageLayout
    title="My Position"
    subtitle="Update your designation and reporting supervisor."
    icon="👔"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Self Service' },
      { label: 'My Position' }
    ]"
  >
    <div v-if="loading" class="position-content">
      <div class="hr-chart-card">
        <div class="hr-chart-body hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted">Loading your position details...</p>
        </div>
      </div>
    </div>

    <div v-else class="position-content">
      <!-- Current summary -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header hr-chart-header--stacked">
          <h5 class="hr-chart-title">
            <i class="bx bx-id-card"></i> Current Details
          </h5>
        </div>
        <div class="hr-chart-body">
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Employee</span>
              <strong class="summary-value">{{ profile.employeeName || '—' }}</strong>
              <small v-if="profile.empNo" class="summary-meta">{{ profile.empNo }}</small>
            </div>
            <div class="summary-item">
              <span class="summary-label">Department</span>
              <strong class="summary-value">{{ profile.departmentName || '—' }}</strong>
            </div>
            <div class="summary-item">
              <span class="summary-label">Position</span>
              <strong class="summary-value">{{ form.positionTitle || 'Not set' }}</strong>
            </div>
            <div class="summary-item">
              <span class="summary-label">Supervisor</span>
              <strong class="summary-value">{{ selectedSupervisorLabel || 'Not set' }}</strong>
              <small v-if="pendingClaim" class="summary-meta pending-note">Awaiting confirmation from {{ pendingClaim.supervisor_name }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit form -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-chart-header--stacked">
          <h5 class="hr-chart-title">
            <i class="bx bx-edit-alt"></i> Update Supervisor
          </h5>
          <p class="hr-chart-subtitle">
            Search for your supervisor from the employee register.
          </p>
        </div>
        <div class="hr-chart-body">
          <div v-if="pendingClaim" class="hr-alert hr-alert-warning hr-mb-3" style="display: flex; justify-content: space-between; align-items: center; gap: 10px;">
            <div>
              Your request is waiting for <strong>{{ pendingClaim.supervisor_name }}</strong> to approve or reject.
              You are not recorded under them until they approve.
            </div>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" :disabled="saving" @click="cancelRequest">
              Cancel Request
            </button>
          </div>
          <div v-else-if="lastRejection" class="hr-alert hr-alert-danger hr-mb-3">
            {{ lastRejection.supervisor_name || 'The selected officer' }} rejected your supervisor claim.
            <span v-if="lastRejection.reject_reason"> Reason: {{ lastRejection.reject_reason }}</span>
            Please select the correct supervisor below and submit again.
          </div>
          <form @submit.prevent="saveInfo" class="position-form">
            <!-- Supervisor picker -->
            <div class="picker-field">
              <label class="hr-form-label">
                Supervisor / Reporting To <span class="required">*</span>
              </label>
              <div class="picker-wrap" v-click-outside="closeSupervisorDropdown">
                <div class="hr-search-wrap">
                  <input
                    v-model.trim="supervisorSearch"
                    type="text"
                    class="hr-input"
                    placeholder="Search employees by name, emp no, or ID..."
                    autocomplete="off"
                    @focus="supervisorDropdownOpen = true"
                  >
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
                <div v-if="supervisorDropdownOpen" class="picker-dropdown">
                  <div v-if="supervisorSearch.length < 2" class="picker-empty">
                    Type at least 2 characters to search the employee register.
                  </div>
                  <div v-else-if="searchingSupervisors" class="picker-empty">
                    <div class="hr-spinner small"></div> Searching...
                  </div>
                  <template v-else-if="supervisorResults.length">
                    <button
                      v-for="emp in supervisorResults"
                      :key="'sup-' + emp.id"
                      type="button"
                      class="picker-option"
                      :class="{ active: form.reportingTo === emp.nationalId }"
                      @mousedown.prevent
                      @click="selectSupervisor(emp)"
                    >
                      <span class="picker-option-title">{{ employeeLabel(emp) }}</span>
                      <span class="picker-option-meta">
                        {{ emp.empNo || '—' }}
                        <span v-if="emp.departmentName"> · {{ emp.departmentName }}</span>
                        <span v-if="emp.positionTitle"> · {{ emp.positionTitle }}</span>
                      </span>
                    </button>
                  </template>
                  <div v-else class="picker-empty">
                    No employees match "{{ supervisorSearch }}".
                  </div>
                </div>
              </div>
              <div v-if="selectedSupervisor" class="picker-selected">
                <i class="bx bx-user-check"></i>
                <span class="picker-selected-text">
                  <strong>{{ employeeLabel(selectedSupervisor) }}</strong>
                  <span class="picker-selected-meta">
                    · {{ selectedSupervisor.empNo || selectedSupervisor.nationalId }}
                  </span>
                </span>
                <button type="button" class="picker-clear" @click="clearSupervisor" title="Clear">
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <span class="hr-form-hint">
                Your supervisor is used for leave approvals and reporting lines.
              </span>
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="hr-btn hr-btn-outline-secondary"
                :disabled="saving"
                @click="resetForm"
              >
                Reset
              </button>
              <button
                type="submit"
                class="hr-btn hr-btn-primary"
                :disabled="saving || !canSave"
              >
                <span v-if="saving" class="hr-spinner small hr-mr-2"></span>
                <i v-else class="bx bx-save hr-mr-2"></i>
                {{ saving ? 'Saving...' : 'Request supervisor confirmation' }}
              </button>
            </div>

            <div
              v-if="message"
              :class="['hr-alert hr-mt-3', isError ? 'hr-alert-danger' : 'hr-alert-success']"
            >
              {{ message }}
            </div>
          </form>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

const clickOutside = {
  beforeMount(el, binding) {
    el._clickOutside = (event) => {
      if (!el.contains(event.target)) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el._clickOutside);
  },
  unmounted(el) {
    document.removeEventListener("click", el._clickOutside);
  },
};

export default {
  name: "EmployeePositionInfo",
  components: { HrPageLayout },
  directives: { clickOutside },
  data() {
    return {
      loading: false,
      saving: false,
      loadingPositions: false,
      searchingSupervisors: false,
      message: "",
      isError: false,
      profile: {
        employeeName: "",
        empNo: "",
        departmentName: "",
      },
      form: {
        positionId: "",
        positionTitle: "",
        reportingTo: "",
      },
      savedForm: null,
      savedSupervisor: null,
      positions: [],
      positionSearch: "",
      positionDropdownOpen: false,
      selectedSupervisor: null,
      supervisorSearch: "",
      supervisorResults: [],
      supervisorDropdownOpen: false,
      supervisorSearchTimer: null,
      skipSupervisorSearch: false,
      pendingClaim: null,
      lastRejection: null,
    };
  },
  computed: {
    selectedPosition() {
      if (!this.form.positionId) return null;
      return this.positions.find((p) => String(p.id) === String(this.form.positionId)) || null;
    },
    filteredPositions() {
      const q = (this.positionSearch || "").trim().toLowerCase();
      const list = Array.isArray(this.positions) ? this.positions : [];
      if (!q) return list.slice(0, 50);
      return list.filter((pos) => {
        const hay = [
          pos.positionName,
          pos.name,
          pos.code,
          pos.description,
        ]
          .map((v) => String(v || "").toLowerCase())
          .join(" ");
        return hay.includes(q);
      }).slice(0, 50);
    },
    selectedSupervisorLabel() {
      if (this.selectedSupervisor) return this.employeeLabel(this.selectedSupervisor);
      return "";
    },
    canSave() {
      return Boolean(this.form.reportingTo);
    },
  },
  watch: {
    supervisorSearch(val) {
      if (this.skipSupervisorSearch) {
        this.skipSupervisorSearch = false;
        return;
      }
      if (this.supervisorSearchTimer) clearTimeout(this.supervisorSearchTimer);
      const query = String(val || "").trim();
      if (query.length < 2) {
        this.supervisorResults = [];
        return;
      }
      if (this.selectedSupervisor && query === this.employeeLabel(this.selectedSupervisor)) {
        this.supervisorResults = [];
        this.supervisorDropdownOpen = false;
        return;
      }
      this.supervisorSearchTimer = setTimeout(() => {
        this.searchSupervisors(query);
      }, 300);
    },
  },
  mounted() {
    this.bootstrap();
  },
  beforeUnmount() {
    if (this.supervisorSearchTimer) clearTimeout(this.supervisorSearchTimer);
  },
  methods: {
    employeeLabel(emp) {
      if (!emp) return "";
      if (emp.employeeName) return emp.employeeName;
      return [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ");
    },
    async bootstrap() {
      this.loading = true;
      try {
        await Promise.all([this.loadPositions(), this.loadInfo()]);
      } finally {
        this.loading = false;
      }
    },
    async loadPositions() {
      this.loadingPositions = true;
      try {
        const data = new FormData();
        data.append("function", "getPositions");
        const res = await execute(data, authUrl);
        const list = res?.data?.data?.positions ?? res?.data?.positions ?? [];
        this.positions = Array.isArray(list) ? list : [];
      } catch (err) {
        console.error("Failed to load positions", err);
        this.positions = [];
      } finally {
        this.loadingPositions = false;
      }
    },
    async loadInfo() {
      this.message = "";
      try {
        const data = new FormData();
        data.append("function", "getEmployeePositionInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          const info = res.data.data || {};
          this.profile = {
            employeeName: info.employeeName || "",
            empNo: info.empNo || "",
            departmentName: info.departmentName || "",
          };
          this.form.positionId = info.positionId ? String(info.positionId) : "";
          this.form.positionTitle = info.positionTitle || "";
          this.form.reportingTo = info.reportingTo || "";
          this.selectedSupervisor = info.supervisor || null;
          this.pendingClaim = info.pendingClaim || null;
          this.lastRejection = info.lastRejection || null;
          if (this.pendingClaim) {
            this.form.reportingTo = this.pendingClaim.supervisor_national_id || this.form.reportingTo;
            this.selectedSupervisor = {
              nationalId: this.pendingClaim.supervisor_national_id,
              empNo: this.pendingClaim.supervisor_emp_no,
              employeeName: this.pendingClaim.supervisor_name,
              firstName: this.pendingClaim.supervisor_name,
            };
          }
          if (this.selectedSupervisor) {
            this.supervisorSearch = this.employeeLabel(this.selectedSupervisor);
          }
          if (this.form.positionTitle) {
            this.positionSearch = this.form.positionTitle;
          }
          this.savedForm = { ...this.form };
          this.savedSupervisor = this.selectedSupervisor ? { ...this.selectedSupervisor } : null;
        }
      } catch (err) {
        console.error("Failed to load position info", err);
        this.message = "Could not load your position details.";
        this.isError = true;
      }
    },
    async searchSupervisors(query) {
      this.searchingSupervisors = true;
      try {
        const data = new FormData();
        data.append("function", "searchEmployeeRegister");
        data.append("search", query);
        const res = await execute(data, authUrl);
        const list = res?.data?.data?.employees ?? res?.data?.employees ?? [];
        this.supervisorResults = Array.isArray(list) ? list : [];
        this.supervisorDropdownOpen = true;
      } catch (err) {
        console.error("Supervisor search failed", err);
        this.supervisorResults = [];
      } finally {
        this.searchingSupervisors = false;
      }
    },
    selectPosition(pos) {
      this.form.positionId = String(pos.id);
      this.form.positionTitle = pos.positionName || pos.name || "";
      this.positionSearch = this.form.positionTitle;
      this.positionDropdownOpen = false;
    },
    clearPosition() {
      this.form.positionId = "";
      this.form.positionTitle = "";
      this.positionSearch = "";
    },
    selectSupervisor(emp) {
      this.skipSupervisorSearch = true;
      this.selectedSupervisor = emp;
      this.form.reportingTo = emp.nationalId || "";
      this.supervisorSearch = this.employeeLabel(emp);
      this.supervisorResults = [];
      this.supervisorDropdownOpen = false;
    },
    clearSupervisor() {
      this.selectedSupervisor = null;
      this.form.reportingTo = "";
      this.supervisorSearch = "";
      this.supervisorResults = [];
    },
    closePositionDropdown() {
      this.positionDropdownOpen = false;
    },
    closeSupervisorDropdown() {
      this.supervisorDropdownOpen = false;
    },
    resetForm() {
      if (this.savedForm) {
        this.form = { ...this.savedForm };
        this.positionSearch = this.form.positionTitle || "";
        this.selectedSupervisor = this.savedSupervisor ? { ...this.savedSupervisor } : null;
        this.supervisorSearch = this.selectedSupervisor
          ? this.employeeLabel(this.selectedSupervisor)
          : "";
      } else {
        this.clearPosition();
        this.clearSupervisor();
      }
      this.message = "";
      this.isError = false;
    },
    async saveInfo() {
      if (!this.canSave) return;
      this.saving = true;
      this.message = "";
      this.isError = false;
      try {
        const data = new FormData();
        data.append("function", "saveEmployeePositionInfo");
        data.append("reportingTo", this.form.reportingTo);
        const res = await execute(data, authUrl);

        if (res?.data?.success) {
          const saved = res.data.data || {};
          this.form.reportingTo = saved.reportingTo || this.form.reportingTo;
          this.pendingClaim = saved.pendingClaim || this.pendingClaim;
          this.lastRejection = null;
          if (saved.supervisor) {
            this.selectedSupervisor = saved.supervisor;
            this.supervisorSearch = this.employeeLabel(saved.supervisor);
          }
          this.positionSearch = this.form.positionTitle;
          this.savedForm = { ...this.form };
          this.savedSupervisor = this.selectedSupervisor ? { ...this.selectedSupervisor } : null;
          this.message = res.data.message || "Request sent to the selected supervisor.";
          this.isError = false;
        } else {
          this.message = res?.data?.message || "Failed to save.";
          this.isError = true;
        }
      } catch (err) {
        console.error("Save error", err);
        this.message = "Network error. Please try again.";
        this.isError = true;
      } finally {
        this.saving = false;
        setTimeout(() => { this.message = ""; }, 5000);
      }
    },
    async cancelRequest() {
      if (!this.pendingClaim) return;
      if (!window.confirm("Are you sure you want to cancel your pending supervisor request?")) return;
      this.saving = true;
      this.message = "";
      this.isError = false;
      try {
        const data = new FormData();
        data.append("function", "cancelSupervisorClaim");
        data.append("id", this.pendingClaim.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.pendingClaim = null;
          this.message = "Pending request cancelled.";
          this.isError = false;
        } else {
          this.message = res?.data?.message || "Failed to cancel request.";
          this.isError = true;
        }
      } catch (err) {
        console.error("Cancel error", err);
        this.message = "Network error while cancelling.";
        this.isError = true;
      } finally {
        this.saving = false;
        setTimeout(() => { this.message = ""; }, 5000);
      }
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.position-content {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: border-box;
}

.hr-chart-header--stacked {
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.hr-chart-subtitle {
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #64748b;
  max-width: 100%;
}

.position-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.summary-meta {
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.4;
}

.picker-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.picker-wrap {
  position: relative;
  width: 100%;
}

.picker-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
}

.picker-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  box-sizing: border-box;
}

.picker-option:last-child {
  border-bottom: 0;
}

.picker-option:hover,
.picker-option.active {
  background: rgba(5, 150, 105, 0.08);
}

.picker-option-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
  width: 100%;
}

.picker-option-meta {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
  width: 100%;
}

.picker-empty {
  padding: 16px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.picker-selected {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.45;
  box-sizing: border-box;
}

.picker-selected i {
  color: #059669;
  font-size: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}

.picker-selected-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.picker-selected-meta {
  color: #64748b;
  font-weight: 500;
}

.picker-clear {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 2px;
  line-height: 1;
  flex-shrink: 0;
}

.picker-clear:hover {
  color: #ef4444;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.hr-alert {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.hr-alert-success {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #065f46;
}

.hr-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.hr-alert-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}
.pending-note { color: #b45309; }

@media (max-width: 768px) {
  .position-content {
    max-width: 100%;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .hr-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
